/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.

Responsible for form validation utilities
*/

import { approval } from '@/vendor/approvejs'
import update from 'immutability-helper'
import formData from './formData'
import './validationTest'

interface IValidate {
    isValid: (state: any) => any
    isInvalid: any
    input: (field: string, val: any, form: any, soil?: any) => any
    form: (_form: any) => any
    errors: (field: any) => any
    addTest: (test: any, name: any) => any
}

const Validate: IValidate = (function () {
    const input = (field: string, val: any, form: any, soil?: any) => {
        if (field.match(/.+?(?=\[)/)) {
            return isArray(field, val, form, soil)
        }
        const name = field
        const { state } = form[name]
        formData.setFormData(name, val)
        if (state === undefined || state === null) {
            form = update(form, { [name]: { state: { $set: {} } } })
        }
        form = update(form, { [name]: { value: { $set: val } } })
        if (soil) {
            form = update(form, {
                [name]: { state: { dirty: { $set: true } } },
            })
        }
        if (form[name].state.dirty) {
            const inputState = approval.value(val, form[name].rules)
            form = update(form, {
                [name]: { state: { approval: { $set: inputState } } },
            })
        }
        return form
    }

    const isArray = (field: any, val: any, form: any, soil: any) => {
        const name = field.match(/.+?(?=\[)/)[0]
        const pos = parseInt(field.match(/\[([^)]+)\]/)[1], 10)
        const { state } = form[name]
        if (state === undefined || state === null) {
            form = update(form, { [name]: { state: { $set: {} } } })
        }
        form = update(form, { [name]: { [pos]: { value: { $set: val } } } })
        if (soil) {
            form = update(form, {
                [name]: { [pos]: { state: { dirty: { $set: true } } } },
            })
        }
        if (form[name][pos].state.dirty) {
            const inputState = approval.value(val, form[name][pos].rules)
            form = update(form, {
                [name]: {
                    [pos]: { state: { approval: { $set: inputState } } },
                },
            })
        }
        return form
    }

    const form = (_form: any) => {
        delete _form.approved
        const keys = Object.keys(_form)
        let formApproved = true
        keys.forEach((name) => {
            if (Array.isArray(_form[name])) {
                for (let index = 0; index < _form[name].length; index++) {
                    _form = input(
                        `${name}[${index}]`,
                        _form[name][index].value,
                        _form,
                        true
                    )
                    formApproved =
                        formApproved &&
                        _form[name][index].state.approval.approved
                }
            } else {
                _form = input(name, _form[name].value, _form, true)
                formApproved =
                    formApproved && _form[name].state.approval.approved
            }
        })
        _form = update(_form, { approved: { $set: formApproved } })
        return _form
    }

    const addTest = (test: any, name: any) => {
        approval.addTest(test, name)
    }

    const isValid = (state: any) => {
        const { approval = {} } = state
        if (approval.approved === true) {
            return true
        }
        return false
    }

    const isInvalid = (state: any) => {
        const { approval = {} } = state
        if (approval.approved === false) {
            return true
        }
        return false
    }

    const errors = (field: any) => {
        const { state = {}, rules = {} } = field
        const { approval = {}, dirty } = state
        const errors: any[] = []
        if (!approval.failed || !dirty) {
            return errors
        }
        approval.failed.forEach((failed: any) => {
            if (!approval[failed].approved) {
                approval[failed].errors.forEach((error: any) => {
                    if (
                        rules &&
                        rules[failed].trans &&
                        typeof rules[failed].trans === 'function'
                    ) {
                        errors.push({
                            rule: failed,
                            message: rules[failed].trans(),
                        })
                    } else {
                        errors.push({ rule: failed, message: error })
                    }
                })
            }
        })
        return errors
    }

    return {
        isValid: isValid,
        isInvalid: isInvalid,
        input: input,
        form: form,
        errors: errors,
        addTest: addTest,
    }
})()

export default Validate
